import { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { theme, typography, spacing, borderRadius, buttonStyles, mergeStyles, isMobile, mobileSpacing } from '../styles/theme';
import { Sentry } from '../sentry';

export default function CardLink({ cardName, isUserMessage }) {
  const [cardImage, setCardImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Fetch card data when hovering
  const fetchCardImage = async () => {
    if (cardImage) {
        return; // Already loaded
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(cardName)}`
      );
      
      // Get image URL - prefer normal, fallback to small
      const imageUrl = response.data.image_uris?.normal || response.data.image_uris?.small;
      setCardImage(imageUrl);
    } catch (error) {
      console.error('Error fetching card:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'CardLink',
          cardName: cardName,
        },
        level: 'warning',
      });
    } finally {
      setLoading(false);
    }
  };

  // Desktop: Hover to preview
  const handleMouseEnter = async (e) => {
    if (isMobile()) return; // Skip on mobile

    setShowPreview(true);
    setMousePos({ x: e.clientX, y: e.clientY });
    await fetchCardImage();
  };

  const handleMouseMove = (e) => {
    if (isMobile()) return;
    setMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseLeave = () => {
    if (isMobile()) return;
    setShowPreview(false);
  };

  // Mobile: Click to show modal
  const handleClick = async (e) => {
    if (!isMobile()) return; // Desktop uses href

    e.preventDefault(); // Prevent navigation on mobile
    await fetchCardImage();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Get Scryfall URL
  const scryfallUrl = `https://scryfall.com/search?q=!"${encodeURIComponent(cardName)}"`;

  // Desktop hover preview
  const preview = showPreview && cardImage && !isMobile() && (
    <div style={{
        position: 'fixed',
        left: mousePos.x + 15,
        top: mousePos.y + 15,
        zIndex: 9999,
        pointerEvents: 'none',
        boxShadow: theme.shadow.large,
        borderRadius: '12px',
        overflow: 'hidden'
    }}>
        <img
            src={cardImage}
            alt={cardName}
            style={{
                width: '250px',
                height: 'auto',
                display: 'block'
            }}
        />
    </div>
  );

  // Mobile modal
  const modal = showModal && (
    <div
      onClick={handleCloseModal}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: mobileSpacing.lg,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme.bg.primary,
          borderRadius: borderRadius.lg,
          padding: mobileSpacing.lg,
          maxWidth: '90%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: mobileSpacing.md,
          boxShadow: theme.shadow.large,
        }}
      >
        {/* Close button */}
        <button
          onClick={handleCloseModal}
          style={{
            alignSelf: 'flex-end',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: theme.text.primary,
            padding: mobileSpacing.sm,
            minWidth: '44px',
            minHeight: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          x
        </button>

        {/* Card name */}
        <h3 style={{
          margin: 0,
          fontSize: typography.fontSize.lg,
          fontWeight: typography.fontWeight.semibold,
          color: theme.text.primary,
          textAlign: 'center',
        }}>
          {cardName}
        </h3>

        {/* Card image */}
        {loading ? (
          <div style={{
            padding: mobileSpacing.xl,
            color: theme.text.secondary,
          }}>
            Loading...
          </div>
        ) : cardImage ? (
          <img
            src={cardImage}
            alt={cardName}
            style={{
              maxWidth: '100%',
              maxHeight: '60vh',
              borderRadius: borderRadius.md,
              boxShadow: theme.shadow.medium,
            }}
          />
        ) : (
          <div style={{
            padding: mobileSpacing.xl,
            color: theme.status.error,
          }}>
            Failed to load card
          </div>
        )}

        {/* View on Scryfall button */}
        <a
          href={scryfallUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            ...mergeStyles(
              buttonStyles.base,
              buttonStyles.primary,
              {
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '48px',
                padding: `${mobileSpacing.md} ${mobileSpacing.lg}`,
                lineHeight: 1,
              }
            ),
          }}
        >
          View on Scryfall →
        </a>

        {/* Tap outside hint */}
        <p style={{
          fontSize: typography.fontSize.xs,
          color: theme.text.tertiary,
          margin: 0,
          textAlign: 'center',
        }}>
          Tap outside to close
        </p>
      </div>
    </div>
  );

  return (
    <>
      <a
        href={scryfallUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          color: isUserMessage ? theme.text.inverse : theme.brand.primary,
          textDecoration: 'underline',
          cursor: 'pointer',
          fontWeight: typography.fontWeight.medium
        }}
      >
        {cardName}
      </a>

      {/* Desktop: hover preview */}
      {showPreview && createPortal(preview, document.body)}

      {/* Mobile: modal */}
      {showModal && createPortal(modal, document.body)}
    </>
  );
}